import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { handleChefSignUp, handleShowProfile } from "../../auth/Auth";
import { handleGetCitites, handleGetAreas } from "../../../services/region";
import MapWithConditionalRendering from "../Map/MapWithConditionRendering";
import { useDispatch, useSelector } from "react-redux";
import { signOutUser } from "../../../store/slice/user";
import SearchableSelect from "../SearchableSelect";

const FormSectionHeading = ({ title, description }) => (
  <div className="mb-6">
    <h3 className="text-sm font-semibold text-gray-200 uppercase tracking-widest">
      {title}
    </h3>
    {description && (
      <p className="mt-1.5 text-sm text-gray-500 leading-relaxed max-w-xl">
        {description}
      </p>
    )}
    <div className="mt-3 h-0.5 w-12 bg-primary rounded-full" />
  </div>
);

const TopBanner = () => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    phone: "",
    profile_pic: "",
    cover_pic: "",
    is_chef: 1,
    longitude: "",
    latitude: "",
    nic_number: "",
    nic_image: "",
    latest_bill: "",
  });

  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isLoadingForm, setIsLoadingForm] = useState(false);
  const [isFetchingUserData, setIsFetchingUserData] = useState(false);

  // Map / location states
  const [coordinates, setCoordinates] = useState({ lat: "", lng: "" });
  const [isLocationConfirmed, setIsLocationConfirmed] = useState(false);

  // City/Area/Address states
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedArea, setSelectedArea] = useState("");
  const [streetAddress, setStreetAddress] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { authToken } = useSelector((state) => state.user);

  // Fetch cities on mount
  useEffect(() => {
    const fetchCities = async () => {
      const data = await handleGetCitites();
      if (data) setCities(data);
    };
    fetchCities();
  }, []);

  // Fetch areas when city changes
  useEffect(() => {
    const fetchAreas = async () => {
      if (!selectedCity) {
        setAreas([]);
        setSelectedArea("");
        return;
      }
      const city = cities.find((c) => c.name === selectedCity);
      if (city) {
        const data = await handleGetAreas(city.id);
        if (data) setAreas(data);
      }
    };
    fetchAreas();
  }, [selectedCity, cities]);

  // Fetch existing user data if logged in
  useEffect(() => {
    const fetchExistingUserData = async () => {
      if (authToken) {
        setIsFetchingUserData(true);
        try {
          const userData = await handleShowProfile(authToken);
          console.log("Existing user data:", userData);

          // Pre-fill form with existing user data
          setFormData((prev) => ({
            ...prev,
            first_name: userData.first_name || "",
            last_name: userData.last_name || "",
            email: userData.email || "",
            phone: userData.phone || "",
          }));
        } catch (error) {
          console.error("Error fetching user profile:", error);
          // Continue with empty form - user might be new or error occurred
        } finally {
          setIsFetchingUserData(false);
        }
      }
    };

    fetchExistingUserData();
  }, [authToken]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (error && !value.includes(" ")) setError("");
    if (emailError) setEmailError("");

    // Don't allow editing of personal info when converting existing user to chef
    if (
      authToken &&
      ["email", "first_name", "last_name", "phone"].includes(name)
    ) {
      return;
    }

    // First update the form data
    setFormData({
      ...formData,
      [name]: value,
    });

    // Then perform validation
    if (name === "password") {
      // Check for spaces in password
      if (value.includes(" ")) {
        setError("Password should not contain spaces");
        return;
      }
      // Check password length only when user stops typing (on blur would be better)
      if (value.length > 0 && value.length < 8) {
        setError("Password must be at least 8 characters long");
        return;
      }
      if (formData.phone === "") {
        setError("Mobile number must be in the format 03XXXXXXXXX");
      }
    }

    // Handle name fields
    if (name === "first_name" || name === "last_name") {
      // Check for spaces
      if (value.includes(" ")) {
        setError(`${name.replace("_", " ")} should not contain spaces`);
        return;
      }
      // Check for special characters using regex (only allow letters and hyphens)
      if (!/^[a-zA-Z-]+$/.test(value)) {
        setError(`${name.replace("_", " ")} should only contain letters`);
        return;
      }
    }

    if (name === "phone") {
      const phoneRegex = /^03\d{9}$/;
      if (!phoneRegex.test(value)) {
        setError("Mobile number must be in the format 03XXXXXXXXX");
      } else {
        setError("");
      }
    } else if (name === "email") {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(value)) {
        setEmailError("Please enter a valid email address.");
      } else {
        setEmailError("");
      }
    }
  };

  // image handling
  const fileInputRef = useRef(null);
  const profileDiv = useRef(null);
  const coverImageRef = useRef(null);
  const nicImageRef = useRef(null);
  const latestBillRef = useRef(null);
  const [isProfile, setIsProfile] = useState("");
  const [isCover, setIsCover] = useState("");
  const [isNicImage, setIsNicImage] = useState("");
  const [isLatestBill, setIsLatestBill] = useState("");

  const handleBoxClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleProfileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, profile_pic: file }));
      const reader = new FileReader();
      reader.onload = () => {
        setIsProfile(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCoverImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, cover_pic: file }));
      const reader = new FileReader();
      reader.onload = () => {
        setIsCover(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleNicImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, nic_image: file }));
      const reader = new FileReader();
      reader.onload = () => {
        setIsNicImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLatestBillChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, latest_bill: file }));
      const reader = new FileReader();
      reader.onload = () => {
        setIsLatestBill(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateEmail = (email) => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields before submission
    if (formData.first_name.includes(" ")) {
      setError("First name should not contain spaces");
      return;
    }
    if (formData.last_name.includes(" ")) {
      setError("Last name should not contain spaces");
      return;
    }
    if (formData.password.includes(" ")) {
      setError("Password should not contain spaces");
      return;
    }
    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }
    if (!/^[a-zA-Z-]+$/.test(formData.first_name)) {
      setError("First name should only contain letters");
      return;
    }
    if (!/^[a-zA-Z-]+$/.test(formData.last_name)) {
      setError("Last name should only contain letters");
      return;
    }
    if (!formData.nic_number.trim()) {
      setError("NIC number is required");
      return;
    }

    if (!emailError && !error) {
      if (!isLocationConfirmed || !coordinates.lat || !coordinates.lng) {
        toast.error("Please confirm your location on the map");
        return;
      }
      if (!selectedCity) {
        toast.error("Please select a city");
        return;
      }
      if (!selectedArea) {
        toast.error("Please select an area");
        return;
      }
      if (!streetAddress.trim()) {
        toast.error("Please enter your street address");
        return;
      }
      if (!formData.profile_pic) {
        profileDiv.current.focus();
        toast.error("Profile picture is required");
        return;
      }
      if (!formData.cover_pic) {
        coverImageRef.current.focus();
        toast.error("Cover photo is required");
        return;
      }
      setIsLoadingForm(true);
      try {
        toast.dismiss();

        const submissionData = {
          ...formData,
          address: `${selectedCity}, ${selectedArea}, ${streetAddress.trim()}`,
          latitude: coordinates.lat,
          longitude: coordinates.lng,
        };
        const res = await handleChefSignUp(submissionData);

        // Show upgrade message
        toast.success(
          "Congratulations! You have been upgraded to Chef. Please sign in again.",
          { autoClose: 3000 }
        );
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "chef_signup",
        });

        // Logout user
        if (authToken) {
          dispatch(signOutUser());
        }

        // Redirect to sign in after few seconds
        setTimeout(() => {
          navigate("/login");
        }, 3200);

        setFormData({
          first_name: "",
          last_name: "",
          email: "",
          password: "",
          phone: "",
          profile_pic: "",
          cover_pic: "",
          is_chef: 1,
          longitude: "",
          latitude: "",
          nic_number: "",
          nic_image: "",
          latest_bill: "",
        });

        setIsProfile("");
        setIsCover("");
        setIsNicImage("");
        setIsLatestBill("");
        setSelectedCity("");
        setSelectedArea("");
        setStreetAddress("");
        setAreas([]);
        setCoordinates({ lat: "", lng: "" });
        setIsLocationConfirmed(false);

        setIsLoadingForm(false);
      } catch (error) {
        toast.error(error.message);
        console.error(error);
        setIsLoadingForm(false);
      }
    }
  };

  // Map callbacks
  const handlePlaceSelect = ({ lat, lng }) => {
    setCoordinates({ lat, lng });
  };

  const handleReverseGeocode = (formattedAddress) => {
    // Address from map is available if needed; we use city/area/street instead
  };

  const handleDragEnd = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setCoordinates({ lat, lng });
  };

  const handleLocationConfirmed = (confirmed) => {
    setIsLocationConfirmed(confirmed);
  };

  // Reusable input style
  const inputBaseClass =
    "w-full rounded-xl border border-white/10 bg-gray-900/60 text-white placeholder-gray-500 focus:border-primary focus:ring-2 focus:ring-primary/40 transition-all duration-200 px-4 py-3";
  const selectBaseClass =
    "w-full rounded-xl border border-white/10 bg-gray-900/60 text-white placeholder-gray-500 focus:border-primary focus:ring-2 focus:ring-primary/40 transition-all duration-200 px-4 py-3 appearance-none";
  const readOnlyClass =
    "bg-gray-700/50 cursor-not-allowed opacity-70 border-gray-600";

  return (
    <>
      <div className="become_aChef min-h-screen bg-gradient-to-b from-[#0f0f1a] to-[#1a1a2e] py-16 lg:px-2 px-4">
        <div className="container mx-auto">
          <form onSubmit={onSubmit}>
            <div className="grid grid-cols-12 lg:gap-x-12 gap-y-10">
              {/* Left column – Form */}
              <div className="lg:col-span-7 col-span-12 lg:order-1 order-2">
                <div className="bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-2xl shadow-2xl shadow-black/40 p-6 md:p-8">
                  {/* Form header */}
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-white tracking-tight">
                      Become a Chef
                    </h2>
                    <p className="mt-1.5 text-sm text-gray-400">
                      Join Shefs Cloud and start sharing your dishes with your community.
                    </p>
                  </div>

                  {isFetchingUserData && (
                    <div className="mb-8 p-4 bg-blue-900/30 text-blue-300 rounded-xl border border-blue-700/50 flex items-center">
                      <svg
                        className="w-5 h-5 mr-3 animate-spin text-blue-400"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 100 8v4a8 8 0 01-8-8z"
                        ></path>
                      </svg>
                      <span>Loading your profile data...</span>
                    </div>
                  )}

                  <div className="space-y-10">
                    {/* 1. Profile & Photos */}
                    <section>
                      <FormSectionHeading
                        title="Profile & Photos"
                        description="Help customers recognise you and your food."
                      />
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                        {/* Profile Photo */}
                        <div className="md:col-span-2">
                          <span className="block text-sm font-medium text-gray-300 mb-3">
                            Profile Photo{" "}
                            <span className="text-primary">*</span>
                          </span>
                          <div
                            ref={profileDiv}
                            role="button"
                            tabIndex={0}
                            aria-label="Upload profile photo"
                            onClick={handleBoxClick}
                            onKeyDown={(e) =>
                              e.key === "Enter" && handleBoxClick()
                            }
                            className={`relative mx-auto w-36 h-36 rounded-full border-2 border-dashed border-gray-600 bg-gray-800/50 overflow-hidden cursor-pointer hover:border-primary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40 transition-colors ${
                              !formData.profile_pic && "focus:border-primary"
                            }`}
                          >
                            {isProfile ? (
                              <img
                                src={isProfile}
                                alt="Profile"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="flex flex-col items-center justify-center h-full text-gray-400 text-sm px-2 text-center">
                                <svg
                                  className="w-8 h-8 mb-1.5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="1.5"
                                    d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="1.5"
                                    d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                </svg>
                                <span>Upload Profile Photo</span>
                              </div>
                            )}
                            <div className="absolute bottom-1 right-1 w-9 h-9 rounded-full bg-primary ring-4 ring-[#1a1a2e] flex items-center justify-center text-white pointer-events-none">
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                              </svg>
                            </div>
                          </div>
                          <input
                            type="file"
                            name="profile_pic"
                            accept="image/*"
                            onChange={handleProfileChange}
                            ref={fileInputRef}
                            className="hidden"
                          />
                          <p className="text-center text-xs text-gray-500 mt-3">
                            Use a clear photo of yourself
                          </p>
                        </div>

                        {/* Cover Photo */}
                        <div className="md:col-span-3">
                          <span className="block text-sm font-medium text-gray-300 mb-3">
                            Cover Photo{" "}
                            <span className="text-primary">*</span>
                          </span>
                          <div
                            role="button"
                            tabIndex={0}
                            aria-label="Upload cover photo"
                            onClick={() => coverImageRef.current.click()}
                            onKeyDown={(e) =>
                              e.key === "Enter" &&
                              coverImageRef.current.click()
                            }
                            className="relative w-full h-36 md:h-40 rounded-xl border-2 border-dashed border-gray-600 bg-gray-800/50 overflow-hidden cursor-pointer hover:border-primary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40 transition-colors"
                          >
                            {isCover ? (
                              <img
                                src={isCover}
                                alt="Cover"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="flex flex-col items-center justify-center h-full text-gray-400 text-sm gap-1.5">
                                <svg
                                  className="w-8 h-8 mb-1"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="1.5"
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                                <span className="font-medium text-gray-300">
                                  Upload Cover Photo
                                </span>
                              </div>
                            )}
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleCoverImageChange}
                            ref={coverImageRef}
                            className="hidden"
                          />
                          <p className="text-xs text-gray-500 mt-3 leading-relaxed">
                            Upload a high-quality horizontal photo that
                            represents you as a chef or showcases your food. This
                            will appear as the cover image on your chef profile.
                          </p>
                        </div>
                      </div>
                    </section>

                    {/* 2. Personal Information */}
                    <section>
                      <FormSectionHeading
                        title="Personal Information"
                        description="Your basic details for your chef account."
                      />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1.5">
                            First Name{" "}
                            <span className="text-primary">*</span>
                          </label>
                          <input
                            type="text"
                            name="first_name"
                            value={formData.first_name}
                            onChange={handleChange}
                            required
                            className={`${inputBaseClass} ${
                              authToken ? readOnlyClass : ""
                            }`}
                            readOnly={!!authToken}
                            disabled={isFetchingUserData}
                            placeholder="First Name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1.5">
                            Last Name{" "}
                            <span className="text-primary">*</span>
                          </label>
                          <input
                            type="text"
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleChange}
                            required
                            className={`${inputBaseClass} ${
                              authToken ? readOnlyClass : ""
                            }`}
                            readOnly={!!authToken}
                            disabled={isFetchingUserData}
                            placeholder="Last Name"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1.5">
                            Email Address{" "}
                            <span className="text-primary">*</span>
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className={`${inputBaseClass} ${
                              authToken ? readOnlyClass : ""
                            }`}
                            readOnly={!!authToken}
                            disabled={isFetchingUserData}
                            placeholder="john@example.com"
                          />
                          {emailError && (
                            <p className="mt-1.5 text-xs text-red-400 bg-red-900/20 px-2.5 py-1.5 rounded-lg">
                              {emailError}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1.5">
                            Password{" "}
                            <span className="text-primary">*</span>
                          </label>
                          <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                            className={inputBaseClass}
                            placeholder="Min 8 characters"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1.5">
                            Phone Number{" "}
                            <span className="text-primary">*</span>
                          </label>
                          <input
                            type="text"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="032xxxxxxxxx"
                            required
                            maxLength={11}
                            pattern="^03[0-9]{9}$"
                            disabled={isFetchingUserData}
                            readOnly={!!authToken}
                            className={`${inputBaseClass} ${
                              authToken ? readOnlyClass : ""
                            }`}
                          />
                          {error && (
                            <p className="mt-1.5 text-xs text-red-400 bg-red-900/20 px-2.5 py-1.5 rounded-lg">
                              {error}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1.5">
                            NIC Number{" "}
                            <span className="text-primary">*</span>
                          </label>
                          <input
                            type="text"
                            name="nic_number"
                            value={formData.nic_number}
                            onChange={handleChange}
                            required
                            className={inputBaseClass}
                            placeholder="12345-1234567-1"
                            maxLength={20}
                          />
                        </div>
                      </div>
                    </section>

                    {/* 3. Location */}
                    <section>
                      <FormSectionHeading
                        title="Location"
                        description="Where customers can find and receive your food."
                      />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1.5">
                            City <span className="text-primary">*</span>
                          </label>
                          <div className="relative">
                            <select
                              value={selectedCity}
                              onChange={(e) => {
                                setSelectedCity(e.target.value);
                                setSelectedArea("");
                              }}
                              className={`${selectBaseClass} pr-10`}
                              required
                            >
                              <option value="">Select City</option>
                              {cities.map((city) => (
                                <option key={city.id} value={city.name}>
                                  {city.name}
                                </option>
                              ))}
                            </select>
                            <svg
                              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1.5">
                            Area <span className="text-primary">*</span>
                          </label>
                          <SearchableSelect
                            options={areas}
                            value={selectedArea}
                            onChange={setSelectedArea}
                            placeholder={selectedCity ? "Select Area" : "Select a city first"}
                            disabled={!selectedCity}
                            required
                            variant="dark"
                            className={`${selectBaseClass} pr-10`}
                          />
                        </div>
                      </div>
                      <div className="mt-5">
                        <label className="block text-sm font-medium text-gray-300 mb-1.5">
                          Street Address{" "}
                          <span className="text-primary">*</span>
                        </label>
                        <input
                          type="text"
                          name="street_address"
                          value={streetAddress}
                          onChange={(e) => setStreetAddress(e.target.value)}
                          required
                          className={inputBaseClass}
                          placeholder="House #, Block, Road"
                        />
                      </div>
                      <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.03] p-4 md:p-5">
                        <MapWithConditionalRendering
                          coordinates={coordinates}
                          onDragEnd={handleDragEnd}
                          showMap={true}
                          onReverseGeocode={handleReverseGeocode}
                          onPlaceSelect={handlePlaceSelect}
                          onLocationConfirmed={handleLocationConfirmed}
                        />
                      </div>
                    </section>

                    {/* 4. Verification Documents */}
                    <section>
                      <FormSectionHeading
                        title="Verification Documents"
                        description="Optional documents to help verify your identity and address."
                      />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {/* NIC Image */}
                        <div>
                          <span className="block text-sm font-medium text-gray-300 mb-3">
                            NIC Image{" "}
                            <span className="text-gray-500">(Optional)</span>
                          </span>
                          <div
                            role="button"
                            tabIndex={0}
                            aria-label="Upload NIC image"
                            onClick={() => nicImageRef.current.click()}
                            onKeyDown={(e) =>
                              e.key === "Enter" &&
                              nicImageRef.current.click()
                            }
                            className="relative w-full h-36 rounded-xl border-2 border-dashed border-gray-600 bg-gray-800/50 overflow-hidden cursor-pointer hover:border-primary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40 transition-colors"
                          >
                            {isNicImage ? (
                              <img
                                src={isNicImage}
                                alt="NIC"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="flex flex-col items-center justify-center h-full text-gray-400 text-sm gap-1.5">
                                <svg
                                  className="w-8 h-8 mb-1"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="1.5"
                                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                  />
                                </svg>
                                <span className="font-medium text-gray-300">
                                  Upload NIC
                                </span>
                                <span className="text-xs text-gray-500">
                                  JPG or PNG
                                </span>
                              </div>
                            )}
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleNicImageChange}
                            ref={nicImageRef}
                            className="hidden"
                          />
                          <p className="text-xs text-gray-500 mt-2">
                            Upload a clear photo of your NIC card.
                          </p>
                        </div>

                        {/* Latest Bill */}
                        <div>
                          <span className="block text-sm font-medium text-gray-300 mb-3">
                            Latest Bill{" "}
                            <span className="text-gray-500">(Optional)</span>
                          </span>
                          <div
                            role="button"
                            tabIndex={0}
                            aria-label="Upload latest bill"
                            onClick={() => latestBillRef.current.click()}
                            onKeyDown={(e) =>
                              e.key === "Enter" &&
                              latestBillRef.current.click()
                            }
                            className="relative w-full h-36 rounded-xl border-2 border-dashed border-gray-600 bg-gray-800/50 overflow-hidden cursor-pointer hover:border-primary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40 transition-colors"
                          >
                            {isLatestBill ? (
                              <img
                                src={isLatestBill}
                                alt="Latest Bill"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="flex flex-col items-center justify-center h-full text-gray-400 text-sm gap-1.5">
                                <svg
                                  className="w-8 h-8 mb-1"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="1.5"
                                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                  />
                                </svg>
                                <span className="font-medium text-gray-300">
                                  Upload Bill
                                </span>
                                <span className="text-xs text-gray-500">
                                  JPG, PNG or PDF
                                </span>
                              </div>
                            )}
                          </div>
                          <input
                            type="file"
                            accept="image/*,application/pdf"
                            onChange={handleLatestBillChange}
                            ref={latestBillRef}
                            className="hidden"
                          />
                          <p className="text-xs text-gray-500 mt-2">
                            To verify NIC address
                          </p>
                        </div>
                      </div>
                    </section>

                    {/* 5. Agreements */}
                    <section>
                      <FormSectionHeading title="Agreements" />
                      <div className="space-y-4">
                        <label className="flex items-start gap-3 cursor-pointer group">
                          <input
                            type="checkbox"
                            required
                            className="mt-0.5 h-5 w-5 rounded border-white/20 bg-gray-900 text-primary accent-primary focus:ring-primary focus:ring-offset-0 shrink-0"
                          />
                          <span className="text-sm text-gray-300 leading-relaxed select-none">
                            I agree to Shefs Cloud's{" "}
                            <Link
                              to="/terms-of-services"
                              onClick={(e) => e.stopPropagation()}
                              className="text-primary font-medium hover:underline"
                            >
                              Terms of Service
                            </Link>
                            , and to receive marketing text messages.
                          </span>
                        </label>
                        <label className="flex items-start gap-3 cursor-pointer group">
                          <input
                            type="checkbox"
                            required
                            className="mt-0.5 h-5 w-5 rounded border-white/20 bg-gray-900 text-primary accent-primary focus:ring-primary focus:ring-offset-0 shrink-0"
                          />
                          <span className="text-sm text-gray-300 leading-relaxed select-none">
                            I acknowledge and agree that I am an independent
                            business and authorized to earn income in Pakistan.
                          </span>
                        </label>
                      </div>
                    </section>

                    {/* 6. Submit */}
                    <div className="border-t border-white/10 pt-8">
                      <button
                        type="submit"
                        disabled={
                          error || emailError || isLoadingForm || isFetchingUserData
                        }
                        className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-10 py-3.5 bg-primary hover:bg-primary/90 text-white font-semibold rounded-xl uppercase tracking-wider transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                      >
                        {isLoadingForm ? (
                          <>
                            <svg
                              className="w-5 h-5 animate-spin"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v4a4 4 0 100 8v4a8 8 0 01-8-8z"
                              ></path>
                            </svg>
                            Loading...
                          </>
                        ) : (
                          <>
                            Submit Application
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M5 12h14M12 5l7 7-7 7"
                              />
                            </svg>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right column – Hero Text */}
              <div className="lg:col-span-5 col-span-12 my-auto lg:order-2 order-1">
                <div className="lg:text-start text-center">
                  <h1 className="font-bold sm:text-5xl text-4xl md:leading-tight leading-snug uppercase text-white tracking-wider">
                    Make money doing what you love
                  </h1>
                  <div className="w-20 h-1 bg-primary my-6 lg:mx-0 mx-auto"></div>
                  <p className="text-xl text-gray-300 lg:mb-4 mb-8 mt-5 max-w-md lg:mx-0 mx-auto">
                    We give you the tools to run your own food business, on your
                    terms.
                  </p>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default TopBanner;